import { type ChangeEvent, type ReactNode } from "react";

import { Input } from "../components/ui/index.js";

import { type PayrollPeriodDraft, rateLabels } from "./payrollProfileModel.js";

export const SelectField = ({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-medium text-foreground">
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-[38px] w-full rounded-xl border border-border bg-surface px-3 text-sm text-foreground transition-colors hover:border-border-strong focus:border-accent focus:outline-none"
    >
      {children}
    </select>
  </div>
);

export const PeriodFields = ({
  idPrefix,
  draft,
  onChange,
}: {
  idPrefix: string;
  draft: PayrollPeriodDraft;
  onChange: (patch: Partial<PayrollPeriodDraft>) => void;
}) => (
  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
    <SelectField
      id={`${idPrefix}-contract`}
      label="Contract type"
      value={draft.contractType}
      onChange={(contractType) =>
        onChange({ contractType, contractualRate: "" })
      }
    >
      <option value="employee">Employee</option>
      <option value="student_agreement">Student agreement</option>
      <option value="freelancer">Freelancer</option>
      <option value="other">Other</option>
    </SelectField>
    <Input
      id={`${idPrefix}-from`}
      label="Effective from"
      type="date"
      required
      value={draft.validFromMonth}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange({ validFromMonth: event.target.value })
      }
      className="tabular-nums"
    />
    <Input
      id={`${idPrefix}-to`}
      label="Effective through"
      type="date"
      value={draft.validToMonth}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange({ validToMonth: event.target.value })
      }
      className="tabular-nums"
    />
    {rateLabels[draft.contractType] && (
      <Input
        id={idPrefix + "-rate"}
        label={rateLabels[draft.contractType]}
        inputMode="decimal"
        pattern="(?:0|[1-9][0-9]{0,9})(?:[.,][0-9]{1,2})?"
        value={draft.contractualRate ?? ""}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange({ contractualRate: event.target.value })
        }
      />
    )}
    <Input
      id={`${idPrefix}-workload`}
      label="Workload (%)"
      type="number"
      min="0.01"
      max="100"
      step="0.01"
      required
      value={draft.workloadPercent}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange({ workloadPercent: event.target.value })
      }
      className="tabular-nums"
    />
    {draft.contractType !== "freelancer" && draft.contractType !== "other" && (
      <>
        <Input
          id={`${idPrefix}-hours`}
          label="Hours per day"
          type="number"
          min="0.01"
          step="0.01"
          required
          value={draft.hoursPerDay}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({ hoursPerDay: event.target.value })
          }
          className="tabular-nums"
        />
        <Input
          id={`${idPrefix}-holiday`}
          label="Holiday entitlement (days/year)"
          type="number"
          min="0"
          step="0.01"
          required
          value={draft.annualHolidayEntitlement}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onChange({ annualHolidayEntitlement: event.target.value })
          }
          className="tabular-nums"
        />
      </>
    )}
  </div>
);
